import {
  BN,
  compareLowerStr,
  formatDecimal,
  isValidAddress,
  useBalance,
} from '@bifrost-platform/bifront-sdk-react-biholder';
import { useWallet } from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import IconTooltipArrow from '@/assets/icons/icon-tooltip-arrow.svg';
import ButtonWithArrow from '@/components/ButtonWithArrow';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import Input from '@/components/atom/Input';
import { StyledText } from '@/components/atom/Text';
import { MEDIA_QUERY_BREAKPOINT } from '@/configs/mediaQuery';
import { TOKEN_SYMBOL_BTCUSD } from '@/configs/tokens/tokenSymbols';
import useBifi from '@/hooks/api/bifi/useBifi';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useEnv from '@/hooks/useEnv';
import { formatAmountDetail, formatRate } from '@/libs/formatNumber';
import { eventBifiDepositClick } from '@/libs/gtag';
import { dialogActionAtom, isShowConnectWalletAtom } from '@/store/uiStore';
import { dashboardCardVariant } from '@/styles/motion';
import { parseNumberInput } from '@/utils/input';
import IconBifi from '../IconBifi';
import Button from '../atom/Button';
import LogoBtcusd from '../atom/LogoBtcusd';
import Skeleton from '../atom/Skeleton';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../atom/Tooltip';
import WarnMessage from '../atom/WarnMessage';

const TooltipKeyframe = keyframes`
  0% {
    transform: translateY(0);
  }


  50% {
    transform: translateY(-10px);
  }


  100% {
    transform: translateY(0);
  }
`;

const CustomTooltip = styled(FlexBox)`
  position: absolute;
  top: -80px;
  left: 140px;
  flex-direction: column;
  animation: ${TooltipKeyframe} 1.5s infinite;

  svg {
    position: absolute;
    bottom: -6px;
    left: 20px;
  }
`;
const CustomTooltipContent = styled.div`
  ${(p) => p.theme.rounded.lg};
  ${(p) => p.theme.bg.grayBlue};
  backdrop-filter: blur(2px);
  padding: 12px 16px;
`;
const InputContainer = styled(FlexBox)`
  flex: 1;
`;
const InputWrapper = styled(FlexBox)`
  position: relative;

  & > label {
    align-self: flex-start;
  }
`;
const Description = styled(StyledText)`
  white-space: pre-line;
`;
const TokenWrapper = styled(FlexBox)`
  ${(p) => p.theme.bg.black};
  ${(p) => p.theme.rounded.lg};
  padding: ${(p) => p.theme.size.lg} ${(p) => p.theme.size.md};
  font-style: italic;
  line-height: 1;
`;
const DepositButton = styled(ButtonWithArrow)`
  align-self: flex-end;
  margin-left: auto;
`;
const StyledGlassPanel = styled(GlassPanel)`
  @media (${MEDIA_QUERY_BREAKPOINT}) {
    min-width: 100%;
  }
`;
const MaxButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`;
const StyledWarnMessage = styled(WarnMessage)`
  width: fit-content;
`;
const Apy = styled.div`
  display: flex;
  align-items: flex-end;
  font-size: ${(p) => p.theme.size.xl};
  ${(p) => p.theme.font.bold}

  & > strong {
    font-size: ${(p) => p.theme.size.xxxl};
    ${(p) => p.theme.font.black}
    color: ${(p) => p.theme.colors.green};
  }

  & > * {
    margin-bottom: -3px;
  }
`;

export default function DepositBifiPanel() {
  // translate
  const { t } = useTranslation();

  // initializer
  const { syncedBifi } = useInitializer();

  // env
  const { isTestnet, tokenBifrostBtcusd } = useEnv();

  // wallet
  const { account } = useWallet();

  // bifi
  const {
    lendingStatuses: statuses,
    rewardApr,
    isLoading: isLoadingBifi,
  } = useBifi();

  // balance
  const { balance } = useBalance(tokenBifrostBtcusd);

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);
  const [, setIsShowConnectWallet] = useAtom(isShowConnectWalletAtom);

  // state
  const [amount, setAmount] = useState('');

  // memo
  const status = useMemo(
    () =>
      statuses.find(({ symbol }) =>
        compareLowerStr(symbol, TOKEN_SYMBOL_BTCUSD)
      ),
    [statuses]
  );
  const depositApr = useMemo(
    () => status?.deposit?.interest?.rate,
    [status?.deposit?.interest?.rate]
  );
  const apr = useMemo(
    () => new BN(rewardApr ?? 0).plus(depositApr ?? 0),
    [depositApr, rewardApr]
  );
  const errorMessage = useMemo(() => {
    if (balance.lte(0)) {
      return t('earn:lackBtcUSD');
    }

    if (balance.lt(amount)) {
      return t('earn:lackBalance');
    }

    return '';
  }, [balance, amount, t]);
  const isConnected = useMemo(() => isValidAddress(account), [account]);
  const isDepositDisable = useMemo(
    () => !!errorMessage || !amount,
    [errorMessage, amount]
  );
  const isErrorMessageShow = useMemo(
    () => account && errorMessage && amount,
    [account, errorMessage, amount]
  );
  const isLoading = useMemo(
    () => !!(account && !syncedBifi && isLoadingBifi),
    [account, syncedBifi, isLoadingBifi]
  );

  // handler
  const handleInputAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setAmount(parseNumberInput(e.target.value)),
    []
  );
  const handleMaxClick = useCallback(() => {
    setAmount(formatDecimal(balance, tokenBifrostBtcusd?.decimals ?? 18));
  }, [balance, tokenBifrostBtcusd?.decimals]);
  const handleDepositClick = useCallback(async () => {
    eventBifiDepositClick(isTestnet);
    setDialogAction({
      isShow: true,
      token: tokenBifrostBtcusd,
      amount: formatDecimal(amount, tokenBifrostBtcusd?.decimals ?? 18),
      type: 'bifi:deposit',
    });
    setAmount('');
  }, [amount, setDialogAction, tokenBifrostBtcusd, isTestnet]);
  const handleConnectWalletClick = useCallback(() => {
    setIsShowConnectWallet(true);
  }, [setIsShowConnectWallet]);

  // effect
  useEffect(() => {
    if (!isConnected) {
      setAmount('');
    }
  }, [isConnected]);

  return (
    <StyledGlassPanel
      as={motion.div}
      variants={dashboardCardVariant}
      $flex
      $direction="column"
      $gap={40}
      $padding="xl"
      $rounded="xl"
    >
      <FlexBox $flex $direction="column" $gap="xl">
        <FlexBox
          $flex
          $gap="xxs"
          $align="center"
          style={{ position: 'relative' }}
        >
          <IconBifi color="#fff" />
          <StyledText as="h2" $fontWeight="bold" $fontSize="xl">
            {t('earn:depositToBifi')}
          </StyledText>
          <CustomTooltip>
            <CustomTooltipContent style={{ whiteSpace: 'pre', left: '110px' }}>
              <Apy>
                APY{' '}
                {isLoading ? (
                  <Skeleton $width={100} />
                ) : (
                  <>
                    <strong>{formatRate(apr)}%</strong>
                  </>
                )}
              </Apy>
            </CustomTooltipContent>
            <IconTooltipArrow />
          </CustomTooltip>
        </FlexBox>
        <Description as="p">{t('earn:depositToBifiDescription')}</Description>
      </FlexBox>
      <FlexBox $flex $gap="md">
        <FlexBox $flex $direction="column" $gap="xs">
          <StyledText $textColor="textGray" $fontWeight="medium">
            {t('earn:depositToken')}
          </StyledText>
          <TokenWrapper $flex $gap={8}>
            <FlexBox $width={148}>
              <LogoBtcusd />
            </FlexBox>
          </TokenWrapper>
        </FlexBox>
        <InputContainer $flex $direction="column" $gap="xl">
          <FlexBox $flex $direction="column" $gap="md">
            <FlexBox $flex $direction="column" $gap={40}>
              <InputWrapper $flex $direction="column" $gap="xs">
                <StyledText $textColor="textGray" $fontWeight="medium">
                  {t('earn:balance')}
                </StyledText>
                {isLoading ? (
                  <Skeleton $height={64} />
                ) : (
                  <>
                    <Input
                      as="div"
                      $padding="md"
                      $textSize="lg"
                      $fontWeight="bold"
                      $rounded="lg"
                    >
                      {formatAmountDetail(balance)}
                    </Input>
                  </>
                )}
              </InputWrapper>
              <InputWrapper $flex $direction="column" $gap="xs">
                <StyledText
                  as="label"
                  htmlFor="amount"
                  $textColor="textGray"
                  $fontWeight="medium"
                >
                  {t('earn:depositAmount')}
                </StyledText>
                <FlexBox style={{ position: 'relative' }}>
                  <Input
                    id="amount"
                    $padding="md"
                    $textSize="lg"
                    $fontWeight="bold"
                    $rounded="lg"
                    style={{ width: '100%', paddingRight: '80px' }}
                    disabled={!isConnected}
                    value={amount}
                    placeholder={t('earn:placeholder')}
                    onChange={handleInputAmountChange}
                  />
                  <MaxButton
                    $padding={{
                      x: 'xs',
                      y: 'xxs',
                    }}
                    $variant="primary"
                    onClick={handleMaxClick}
                    disabled={!account}
                  >
                    Max
                  </MaxButton>
                </FlexBox>
              </InputWrapper>
            </FlexBox>
            {isErrorMessageShow && (
              <StyledWarnMessage padding="xxs" rounded="xs" textSize="xs">
                <span>{errorMessage}</span>
              </StyledWarnMessage>
            )}
          </FlexBox>
          <FlexBox $flex $justify="space-between">
            {!account ? (
              <Tooltip placement="top" offset={16}>
                <TooltipTrigger asChild>
                  <DepositButton
                    padding="md"
                    gap="xxxs"
                    variant="primary"
                    fontWeight="bold"
                    onClick={handleConnectWalletClick}
                  >
                    {t('earn:deposit')}
                  </DepositButton>
                </TooltipTrigger>
                <TooltipContent>
                  <TooltipArrow />
                  <StyledText $fontSize="md" $fontWeight="bold">
                    {t('dashboard:connectWalletFirst')}
                  </StyledText>
                </TooltipContent>
              </Tooltip>
            ) : (
              <DepositButton
                disabled={isDepositDisable}
                padding="md"
                gap="xxxs"
                variant="primary"
                fontWeight="bold"
                onClick={handleDepositClick}
              >
                {t('earn:deposit')}
              </DepositButton>
            )}
          </FlexBox>
        </InputContainer>
      </FlexBox>
    </StyledGlassPanel>
  );
}
